import React, { useState } from "react";
import {
	Container,
	TextField,
	Button,
	Box,
	CircularProgress,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Alert,
	Chip,
	Card,
	CardContent,
	Grid,
	Tooltip,
} from "@mui/material";
import { CSVLink } from "react-csv";
import axios from "axios";
import {
	FaDownload,
	FaCheckCircle,
	FaExclamationTriangle,
	FaInfoCircle,
	FaSearch,
	FaPhone,
	FaCalendarAlt,
} from "react-icons/fa";
import { API_URL } from '../config/api';
import BRANDING from "../config/branding";

// Error code descriptions (kept for backward compatibility if needed)
const errorCodeDescriptions = {
	"000": "Delivered",
	"0dc": "Absent Subscriber",
	206: "Absent Subscriber",
	"21b": "Absent Subscriber",
	"023": "Absent Subscriber",
	"027": "Absent Subscriber",
	"053": "Absent Subscriber",
	"054": "Absent Subscriber",
	"058": "Absent Subscriber",
	439: "Absent subscriber or ported subscriber or subscriber is barred",
	254: "Subscriber's phone inbox is full",
	220: "Subscriber's phone inbox is full",
	120: "Subscriber's phone inbox is full",
	"008": "Subscriber's phone inbox is full",
	255: "Invalid or inactive mobile number or subscriber's phone inbox is full",
	0: "Invalid or inactive mobile number or subscriber's phone inbox is full",
	"20b": "Invalid or inactive mobile number",
	"004": "Invalid or inactive mobile number",
	510: "Invalid or inactive mobile number",
	215: "Invalid or inactive mobile number",
	"20d": "Subscriber is barred on the network",
	130: "Subscriber is barred on the network",
	131: "Subscriber is barred on the network",
	222: "Network operator system failure",
	602: "Network operator system failure",
	306: "Network operator system failure",
	"032": "Network operator system failure or operator not supported",
	"085": "Subscriber is on DND",
	"065": "Message content or senderID is blocked on the promotional route",
	600: "Message content or senderID is blocked on the promotional route",
	"40a": "SenderID not whitelisted on the account",
	"082": "Network operator not supported",
	"00a": "SenderID is restricted by the operator",
	"078": "Restricted message content or senderID is blocked.",
	432: "Restricted message content or senderID is blocked.",
};

// Helpers
const getErrorMessage = (code) => errorCodeDescriptions[code] || "Unknown";
const formatPhone = (msisdn) =>
	msisdn.startsWith("234") ? "0" + msisdn.slice(3) : msisdn;

const getStatusColor = (deliveryStatus) => {
	switch (deliveryStatus) {
		case "success":
			return "success";
		case "failed":
			return "error";
		default:
			return "warning";
	}
};

const getStatusIcon = (deliveryStatus) => {
	switch (deliveryStatus) {
		case "success":
			return <FaCheckCircle />;
		case "failed":
			return <FaExclamationTriangle />;
		default:
			return <FaInfoCircle />;
	}
};

const MessageSearch = () => {
	const [phoneNumber, setPhoneNumber] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [searchInfo, setSearchInfo] = useState(null);

	const normalizePhoneNumber = (number) => {
		let cleaned = number.replace(/\D/g, "");
		if (cleaned.startsWith("0")) {
			return "234" + cleaned.slice(1);
		} else if (cleaned.startsWith("234")) {
			return cleaned;
		} else {
			return cleaned;
		}
	};

	const handleSearch = async () => {
		if (!phoneNumber) {
			setError("Phone number is required");
			return;
		}

		try {
			setLoading(true);
			setError("");
			setResults([]);
			setSearchInfo(null);
			const normalizedPhone = normalizePhoneNumber(phoneNumber);
			const token = localStorage.getItem("token");

			const response = await axios.get(
    `${API_URL}/search.php`,
    {
        params: {
            phoneNumber: normalizedPhone,
            startDate,
            endDate,
        },
        headers: {
            Authorization: `Bearer ${token}`,
        },
    }
);

			if (response.data.success) {
				setResults(response.data.data || []);
				setSearchInfo({
					count: response.data.count || 0,
					phone_number: response.data.phone_number,
					date_range: response.data.date_range,
				});
			} else {
				setError(response.data.message || "Failed to fetch data");
			}
		} catch (error) {
			if (error.response) {
				setError(
					error.response.data.message ||
						error.response.data.error ||
						"Failed to fetch data"
				);
			} else if (error.request) {
				setError("Network error. Please check your connection.");
			} else {
				setError("An unexpected error occurred.");
			}
		} finally {
			setLoading(false);
		}
	};

	const transformDataForCSV = (data) =>
		data.map((row) => ({
			msisdn: "'" + row.msisdn,
			network: row.network,
			senderid: row.senderid || "UBA",
			created_at: row.created_at,
			error_code: row.dlr_request,
			description: row.status_description || getErrorMessage(row.dlr_request),
			delivery_status: row.delivery_status || "unknown",
			dlr_status: row.dlr_status,
			message: row.text,
			requestType: "SMS",
		}));

	return (
		<Box
			sx={{
				bbackground: "linear-gradient(135deg, #ffffff 0%, #f5f9ff 100%)",
				minHeight: "100vh",
				py: 4,
			}}
		>
			<Container maxWidth='lg'>
				<Paper
					elevation={24}
					sx={{
						borderRadius: 4,
						overflow: "hidden",
						background: "rgba(255, 255, 255, 0.98)",
						backdropFilter: "blur(10px)",
					}}
				>
					{/* Header */}
					<Box
						sx={{
							background: `linear-gradient(45deg, ${BRANDING.primaryColor} 30%, #B91C1C 90%)`,
							color: "white",
							p: 4,
							textAlign: "center",
						}}
					>
						<Typography
							variant='h4'
							fontWeight='bold'
							sx={{ mb: 1, textShadow: "0 2px 4px rgba(0,0,0,0.3)" }}
						>
							🔎 RedPay Message Search
						</Typography>
						<Typography variant='subtitle1' sx={{ opacity: 0.9 }}>
							Search and analyze SMS delivery reports
						</Typography>
					</Box>

					<Box p={4}>
						{/* Search Form */}
						<Card elevation={8} sx={{ mb: 4, borderRadius: 3 }}>
							<CardContent>
								<Typography
									variant="h6"
									fontWeight="bold"
									sx={{
										color: BRANDING.primaryColor,
										mb: 3,
									}}
								>
									Search Parameters
								</Typography>
								<Grid container spacing={3} alignItems='center'>
									<Grid item xs={12} sm={4}>
										<TextField
											label='Phone Number'
											variant='outlined'
											fullWidth
											value={phoneNumber}
											onChange={(e) => setPhoneNumber(e.target.value)}
											required
											InputProps={{
												startAdornment: (
													<FaPhone
														style={{ marginRight: 8, color: BRANDING.primaryColor, }}
													/>
												),
											}}
											placeholder='e.g., 08012345678'
										/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<TextField
											label='Start Date'
											type='date'
											fullWidth
											InputLabelProps={{ shrink: true }}
											value={startDate}
											onChange={(e) => setStartDate(e.target.value)}
											InputProps={{
												startAdornment: (
													<FaCalendarAlt
														style={{ marginRight: 8, color: BRANDING.primaryColor }}
													/>
												),
											}}
										/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<TextField
											label='End Date'
											type='date'
											fullWidth
											InputLabelProps={{ shrink: true }}
											value={endDate}
											onChange={(e) => setEndDate(e.target.value)}
											InputProps={{
												startAdornment: (
													<FaCalendarAlt
														style={{ marginRight: 8, color: BRANDING.primaryColor }}
													/>
												),
											}}
										/>
									</Grid>
									<Grid item xs={12} sm={2}>
										<Button
											variant='contained'
											fullWidth
											onClick={handleSearch}
											disabled={loading}
											startIcon={
												loading ? <CircularProgress size={16} /> : <FaSearch />
											}
											sx={{
												height: 56,
												borderRadius: 2,
												fontWeight: 600,
												background:
													`linear-gradient(45deg, ${BRANDING.primaryColor} 30%, #B91C1C 90%)`,
												"&:hover": {
													background:
														"linear-gradient(45deg, #B91C1C 30%, #991B1B 90%)",
												},
											}}
										>
											{loading ? "Searching..." : "Search"}
										</Button>
									</Grid>
								</Grid>
							</CardContent>
						</Card>

						{/* Error Alert */}
						{error && (
							<Alert
							severity='warning'
							sx={{
								mb: 3,
								borderRadius: 2,
								backgroundColor: "#EEF4FF",
								color: "#1E2F97",
								border: "1px solid #1E2F97",

								"& .MuiAlert-icon": {
									color: "#1E2F97",
								},
							}}
							onClose={() => setError("")}
						>
							{error}
						</Alert>
						)}

						{/* Search Info */}
						{searchInfo && (
							<Card elevation={4} sx={{ mb: 3, borderRadius: 2 }}>
								<CardContent>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={4}>
											<Typography variant='body2' color='textSecondary'>
												Search Results
											</Typography>
											<Typography
												variant='h6'
												fontWeight='bold'
												sx={{ color: BRANDING.primaryColor }}
											>
												{searchInfo.count} messages found
											</Typography>
										</Grid>
										<Grid item xs={12} sm={4}>
											<Typography variant='body2' color='textSecondary'>
												Phone Number
											</Typography>
											<Typography variant='h6' fontWeight='bold'>
												{formatPhone(searchInfo.phone_number)}
											</Typography>
										</Grid>
										<Grid item xs={12} sm={4}>
											<Typography variant='body2' color='textSecondary'>
												Date Range
											</Typography>
											<Typography variant='h6' fontWeight='bold'>
												{searchInfo.date_range.start &&
												searchInfo.date_range.end
													? `${searchInfo.date_range.start} to ${searchInfo.date_range.end}`
													: "All dates"}
											</Typography>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						)}

						{/* Export Button */}
						{results.length > 0 && (
							<Box display='flex' justifyContent='flex-end' mb={3}>
								<CSVLink
									data={transformDataForCSV(results)}
									filename={`SMS_Report_${
										new Date().toISOString().split("T")[0]
									}.csv`}
									style={{ textDecoration: "none" }}
								>
									<Button
										variant='contained'
										startIcon={<FaDownload />}
										sx={{
											background:
												`linear-gradient(45deg, ${BRANDING.primaryColor} 30%, #B91C1C 90%)`,
											borderRadius: 2,
											fontWeight: 600,
											px: 3,
											"&:hover": {
												background:
													"linear-gradient(45deg, #16257A 30%, #1E40AF 90%)",
											},
										}}
									>
										Export Report
									</Button>
								</CSVLink>
							</Box>
						)}

						{/* Results Table */}
						{loading ? (
							<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
								<Box textAlign='center'>
									<CircularProgress size={60} thickness={4} color='error' />
									<Typography
										variant='h6'
										sx={{ mt: 2, color: "text.secondary" }}
									>
										Searching messages...
									</Typography>
								</Box>
							</Box>
						) : results.length > 0 ? (
							<Card elevation={8} sx={{ borderRadius: 3, overflow: "hidden" }}>
								<TableContainer>
									<Table sx={{ minWidth: 650 }}>
										<div className='flex justify-center mb-6'>
	<img
		src={BRANDING.logo}
		alt={BRANDING.shortName}
		className='h-16 w-auto object-contain'
	/>
</div>
										<TableHead>
											<TableRow sx={{  backgroundColor: "#FEF2F2"}}>
												<TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>
													Description
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>
													DLR Status
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Network</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>
													Sender ID
												</TableCell>
												<TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{results.map((msg) => (
												<TableRow
													key={msg.id}
													sx={{
														"&:hover": {
															backgroundColor: "rgba(215, 25, 32, 0.05)",
														},
														"&:nth-of-type(odd)": {
															backgroundColor: "rgba(0, 0, 0, 0.02)",
														},
													}}
												>
													<TableCell>
														<Typography variant='body2' fontWeight={500}>
															{formatPhone(msg.msisdn)}
														</Typography>
													</TableCell>
													<TableCell>
														<Tooltip title={msg.text} arrow>
															<Typography variant='body2'>
																{msg.text.length > 30
																	? `${msg.text.slice(0, 30)}...`
																	: msg.text}
															</Typography>
														</Tooltip>
													</TableCell>
													<TableCell>
														<Chip
															icon={getStatusIcon(msg.delivery_status)}
															label={msg.dlr_request}
															color={getStatusColor(msg.delivery_status)}
															size='small'
															sx={{ fontWeight: 600 }}
														/>
													</TableCell>
													<TableCell>
														<Typography variant='body2'>
															{msg.status_description ||
																getErrorMessage(msg.dlr_request)}
														</Typography>
													</TableCell>
													<TableCell>
														<Chip
															label={msg.dlr_status ?? "Pending"}
															color={
																msg.dlr_status === "delivered"
																	? "success"
																	: "default"
															}
															size='small'
															variant='outlined'
														/>
													</TableCell>
													<TableCell>
														<Typography variant='body2' fontWeight={500}>
															{msg.network}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant='body2'>
															{msg.senderid}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant='body2'>
															{new Date(msg.created_at).toLocaleString()}
														</Typography>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Card>
						) : (
							!loading &&
							searchInfo && (
								<Card
									elevation={4}
									sx={{ borderRadius: 2, textAlign: "center", py: 6 }}
								>
									<Typography variant='h6' color='textSecondary'>
										No messages found for this search criteria
									</Typography>
									<Typography
										variant='body2'
										color='textSecondary'
										sx={{ mt: 1 }}
									>
										Try adjusting your search parameters
									</Typography>
								</Card>
							)
						)}
					</Box>
				</Paper>
			</Container>
		</Box>
	);
};

export default MessageSearch;
